/* eslint-disable */
// Auto-generated WebSocket client from AsyncAPI spec
// DO NOT EDIT MANUALLY - Run 'npm run generate:socket-client' to regenerate
import { Socket, io } from "socket.io-client";
import { logger } from "@/logger";
import type {
  ConnectedPayload,
  DisconnectedPayload,
  ErrorPayload,
  IncomingWebSocketEvent,
  NotificationBadgeDto,
  NotificationBatchDto,
  NotificationEventDto,
  OutgoingWebSocketEvent,
  PlanningVersionApprovedEventDto,
  PlanningVersionCreatedEventDto,
  PlanningVersionDeletedEventDto,
  PlanningVersionRevertedEventDto,
  PlanningVersionUpdatedEventDto,
  PongDto,
  ShiftEventDto,
  SolverCompletedEventDto,
  SolverFailedEventDto,
  SolverProgressEventDto,
} from "./types";

export interface WebSocketClientOptions {
  url?: string;
  token?: string;
  autoConnect?: boolean;
  reconnection?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
}

export type EventHandler<T = any> = (data: T) => void;

export class WebSocketClient {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<EventHandler>> = new Map();
  private options: WebSocketClientOptions;

  constructor(options: WebSocketClientOptions = {}) {
    this.options = {
      url:
        (import.meta.env?.VITE_WS_URL as string | undefined) ||
        (import.meta.env?.VITE_BACKEND_URL as string | undefined) ||
        "wss://api.heyboomer.localhost",
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      ...options,
    };

    if (this.options.autoConnect) {
      this.connect();
    }
  }

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    // Convert http(s) to ws(s) protocol while preserving path (e.g., /version/1.1.3)
    const wsUrl = this.options.url!.replace("https:", "wss:").replace("http:", "ws:");

    this.socket = io(`${wsUrl}/admin`, {
      transports: ["polling", "websocket"],
      withCredentials: true,
      auth: this.options.token ? { token: this.options.token } : undefined,
      reconnection: this.options.reconnection,
      reconnectionAttempts: this.options.reconnectionAttempts,
      reconnectionDelay: this.options.reconnectionDelay,
    });

    this.setupEventHandlers();
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on("connect", () => {
      logger.info("[WebSocket] Connected");
      this.emit("connected", { socketId: this.socket!.id, userId: "" });
    });

    this.socket.on("disconnect", (reason: string) => {
      logger.info("[WebSocket] Disconnected:", reason);
      this.emit("disconnected", { socketId: this.socket!.id, reason });
    });

    this.socket.on("error", (error: Error) => {
      logger.error("[WebSocket] Error:", error);
      this.emit("error", { message: error.message || "Unknown error", error: error.toString() });
    });

    // Generated event handlers

    this.socket.on("notification:new", (data: NotificationEventDto) => {
      logger.info("[WebSocket] Notification New:", data);
      this.emit("notification:new", data);
    });

    this.socket.on("notification:badge", (data: NotificationBadgeDto) => {
      logger.info("[WebSocket] Notification Badge:", data);
      this.emit("notification:badge", data);
    });

    this.socket.on("notification:batch", (data: NotificationBatchDto) => {
      logger.info("[WebSocket] Notification Batch:", data);
      this.emit("notification:batch", data);
    });

    this.socket.on("shift:changed", (data: ShiftEventDto) => {
      logger.info("[WebSocket] Shift Changed:", data);
      this.emit("shift:changed", data);
    });

    this.socket.on("shifts:changed", (data: ShiftEventDto) => {
      logger.info("[WebSocket] Shifts Changed:", data);
      this.emit("shifts:changed", data);
    });

    this.socket.on("planning:version-created", (data: PlanningVersionCreatedEventDto) => {
      logger.info("[WebSocket] Planning Version-created:", data);
      this.emit("planning:version-created", data);
    });

    this.socket.on("planning:version-updated", (data: PlanningVersionUpdatedEventDto) => {
      logger.info("[WebSocket] Planning Version-updated:", data);
      this.emit("planning:version-updated", data);
    });

    this.socket.on("planning:version-approved", (data: PlanningVersionApprovedEventDto) => {
      logger.info("[WebSocket] Planning Version-approved:", data);
      this.emit("planning:version-approved", data);
    });

    this.socket.on("planning:version-reverted", (data: PlanningVersionRevertedEventDto) => {
      logger.info("[WebSocket] Planning Version-reverted:", data);
      this.emit("planning:version-reverted", data);
    });

    this.socket.on("planning:version-deleted", (data: PlanningVersionDeletedEventDto) => {
      logger.info("[WebSocket] Planning Version-deleted:", data);
      this.emit("planning:version-deleted", data);
    });

    this.socket.on("connected", (data: ConnectedPayload) => {
      logger.info("[WebSocket] Connected:", data);
      this.emit("connected", data);
    });

    this.socket.on("disconnected", (data: DisconnectedPayload) => {
      logger.info("[WebSocket] Disconnected:", data);
      this.emit("disconnected", data);
    });

    this.socket.on("error", (data: ErrorPayload) => {
      logger.info("[WebSocket] Error:", data);
      this.emit("error", data);
    });

    this.socket.on("pong", (data: PongDto) => {
      logger.info("[WebSocket] Pong:", data);
      this.emit("pong", data);
    });

    this.socket.on("solver:progress", (data: SolverProgressEventDto) => {
      logger.info("[WebSocket] Solver Progress:", data);
      this.emit("solver:progress", data);
    });

    this.socket.on("solver:completed", (data: SolverCompletedEventDto) => {
      logger.info("[WebSocket] Solver Completed:", data);
      this.emit("solver:completed", data);
    });

    this.socket.on("solver:failed", (data: SolverFailedEventDto) => {
      logger.info("[WebSocket] Solver Failed:", data);
      this.emit("solver:failed", data);
    });
  }

  // Send events
  send(event: OutgoingWebSocketEvent): void {
    if (!this.socket?.connected) {
      logger.warn("[WebSocket] Not connected, cannot send event:", event.type);
      return;
    }

    this.socket.emit(event.type, event.payload);
  }

  // Convenience methods for sending specific events

  ping(): void {
    this.send({ type: "ping", payload: { timestamp: new Date().toISOString() } });
  }

  // Event listeners with proper typing
  on<T extends IncomingWebSocketEvent["type"]>(
    event: T,
    handler: EventHandler<Extract<IncomingWebSocketEvent, { type: T }>["payload"]>,
  ): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler as EventHandler);
  }

  off<T extends IncomingWebSocketEvent["type"]>(
    event: T,
    handler: EventHandler<Extract<IncomingWebSocketEvent, { type: T }>["payload"]>,
  ): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler as EventHandler);
    }
  }

  private emit(event: string, data: any): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }

  // Getters
  get isConnected(): boolean {
    return this.socket?.connected || false;
  }

  get socketId(): string | undefined {
    return this.socket?.id;
  }
}

// Singleton instance
let wsClient: WebSocketClient | null = null;

export function getWebSocketClient(options?: WebSocketClientOptions): WebSocketClient {
  if (!wsClient) {
    wsClient = new WebSocketClient(options);
  }
  return wsClient;
}

export function disconnectWebSocket(): void {
  if (wsClient) {
    wsClient.disconnect();
    wsClient = null;
  }
}
